import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { CheckOutlined, CloseOutlined, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import slackIcon from '../../../assets/images/slack_icon.svg';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { api, ENDPOINTS } from '../../../services';
import { NOTIFICATION_SETTINGS } from '../../../shared/appConstants';
import {
  connectSlack,
  disconnectSlack,
  fetchSlackProjectConfig,
  fetchSlackStatus,
  fetchSlackUsers,
  fetchSlackUserMappings,
  handleSlackCallback,
  updateSlackUserMappings,
  upsertSlackProjectConfig
} from '../../../store/slackSlice';
import { fetchAllUsers } from '../../../store/userSlice';
import './NotificationSettings.scss';
import '../../dashboard/ProjectStatusModal.scss';

const APP_TOOLTIP_SLOT_PROPS = { tooltip: { className: 'app-tooltip' } };
const TEAM_PAUSE_NOTIFICATION_PERSIST_DEBOUNCE_MS = 650;
const CUSTOM_SLACK_FREQUENCY_REGEX = /^\d+$/;
const CUSTOM_SLACK_FREQUENCY_INPUT_REGEX = /^\d*$/;
const MAX_CUSTOM_FREQUENCY_MINUTES = 10080;
const NO_USERS_AVAILABLE_FOR_MAPPING_MESSAGE = 'No users available for mapping.';

const SLACK_NOTIFICATION_FREQUENCY_OPTIONS = [
  { value: 'all_time', labelKey: 'notificationSettings.slackFrequency.allTime' },
  { value: '3h', labelKey: 'notificationSettings.slackFrequency.everyThreeHours' },
  { value: '5h', labelKey: 'notificationSettings.slackFrequency.everyFiveHours' },
  { value: '8h', labelKey: 'notificationSettings.slackFrequency.everyEightHours' },
  { value: '10h', labelKey: 'notificationSettings.slackFrequency.everyTenHours' },
  { value: '24h', labelKey: 'notificationSettings.slackFrequency.everyTwentyFourHours' },
  { value: 'custom', labelKey: 'notificationSettings.slackFrequency.customTime' }
];

const SLACK_FREQUENCY_TO_MINUTES = {
  all_time: null,
  '3h': 180,
  '5h': 300,
  '8h': 480,
  '10h': 600,
  '24h': 1440
};

const normalizeProjectUser = (user, index) => ({
  id: user?.id || user?.user_id || `user-${index}`,
  name: user?.name || user?.full_name || user?.fullName || '-',
  email: user?.email || user?.user_email || user?.userEmail || '-',
  slackUserId: user?.slack_user_id || user?.slackUserId || null,
  slackEmail: user?.slack_email || user?.slackEmail || null
});

const normalizeSlackUserMapping = (mapping) => ({
  slackUserId: mapping?.slack_user_id || mapping?.slackUserId || '',
  slackName: mapping?.slack_user_name || mapping?.slackUserName || '',
  slackEmail: mapping?.slack_email || mapping?.slackEmail || '',
  pmpiUserId: mapping?.pmpi_user_id || mapping?.pmpiUserId || '',
  pmpiName: mapping?.pmpi_user_name || mapping?.pmpiUserName || '',
  pmpiEmail: mapping?.pmpi_user_email || mapping?.pmpiUserEmail || '',
  isMapped: Boolean(mapping?.is_mapped ?? mapping?.isMapped)
});

const createProjectUsersByIdMap = (projectUsers = []) => new Map(
  projectUsers
    .map(normalizeProjectUser)
    .filter(user => user.id)
    .map(user => [user.id, user])
);

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const areEmailsMatching = (firstEmail, secondEmail) => {
  const left = normalizeEmail(firstEmail);
  const right = normalizeEmail(secondEmail);
  const isValidEmail = (value) => value && value !== '-' && value.includes('@');

  return Boolean(isValidEmail(left) && isValidEmail(right) && left === right);
};

const DEFAULT_DISABLED_NOTIFICATION_ROWS = new Set([
  'comment_on_task',
  'reply_to_comment',
  'task_status_change',
  'worklog_update'
]);

const createDefaultSlackNotificationSettings = () => (
  NOTIFICATION_SETTINGS.TABLE_ROWS.reduce((acc, row) => {
    acc[row.id] = !DEFAULT_DISABLED_NOTIFICATION_ROWS.has(row.id);
    return acc;
  }, {})
);

const buildSlackNotificationSettingPayload = (settings) => (
  NOTIFICATION_SETTINGS.TABLE_ROWS.reduce((acc, row) => {
    acc[row.id] = { slack: Boolean(settings?.[row.id]) };
    return acc;
  }, {})
);

const normalizeProjectSlackConfig = (payload) => payload?.data || payload || null;

const getProjectSlackNotificationSettingsMap = (projectConfig) => (
  projectConfig?.project?.notification_config?.notification_setting
  || projectConfig?.project?.notification_config?.notificationSetting
  || projectConfig?.project?.notificationConfig?.notification_setting
  || projectConfig?.project?.notificationConfig?.notificationSetting
  || projectConfig?.project?.notification_config
  || projectConfig?.project?.notificationConfig
  || projectConfig?.notification_config?.notification_setting
  || projectConfig?.notification_config?.notificationSetting
  || projectConfig?.notificationConfig?.notification_setting
  || projectConfig?.notificationConfig?.notificationSetting
  || projectConfig?.notification_setting
  || projectConfig?.notificationSetting
  || null
);

const normalizeSlackNotificationSettings = (notificationConfig) => (
  (() => {
    const slackNotificationSettingsMap = getProjectSlackNotificationSettingsMap(notificationConfig);
    const hasStoredSlackNotificationSettings = Boolean(
      slackNotificationSettingsMap
      && typeof slackNotificationSettingsMap === 'object'
      && Object.keys(slackNotificationSettingsMap).length > 0
    );

    return NOTIFICATION_SETTINGS.TABLE_ROWS.reduce((acc, row) => {
      const slackValue = slackNotificationSettingsMap?.[row.id]?.slack;
      acc[row.id] = typeof slackValue === 'boolean'
        ? slackValue
        : hasStoredSlackNotificationSettings
          ? false
          : !DEFAULT_DISABLED_NOTIFICATION_ROWS.has(row.id);
      return acc;
    }, {});
  })()
);

const getSlackFrequencySelection = (batchIntervalMinutes) => {
  if (batchIntervalMinutes === null || batchIntervalMinutes === undefined || batchIntervalMinutes === '') {
    return { frequency: 'all_time', customFrequency: '' };
  }

  const parsedMinutes = parseInt(batchIntervalMinutes, 10);
  const presetFrequency = Object.keys(SLACK_FREQUENCY_TO_MINUTES)
    .find(key => SLACK_FREQUENCY_TO_MINUTES[key] === parsedMinutes);

  if (presetFrequency) {
    return { frequency: presetFrequency, customFrequency: '' };
  }

  return {
    frequency: `custom:${parsedMinutes}`,
    customFrequency: Number.isFinite(parsedMinutes) ? String(parsedMinutes) : ''
  };
};

const getCustomSlackFrequencyOptionValue = (minutes) => `custom:${minutes}`;

const formatCustomSlackFrequencyLabel = (minutes) => {
  const parsedMinutes = parseInt(minutes, 10);
  if (!Number.isFinite(parsedMinutes)) {
    return 'Custom Time';
  }

  return `${parsedMinutes} ${parsedMinutes === 1 ? 'min' : 'mins'}`;
};

const createSlackUserMapping = ({
  id = '',
  slackUserId = '',
  slackName = '-',
  slackEmail = '-',
  projectUser = null,
  pmpiUserId = '',
  pmpiName = '',
  pmpiEmail = ''
}) => ({
  id: id || slackUserId,
  slackUserId: slackUserId || id,
  slackName,
  slackEmail,
  pmpiUserId: projectUser?.id || pmpiUserId || '',
  pmpiName: projectUser?.name || pmpiName || '',
  pmpiEmail: projectUser?.email || pmpiEmail || ''
});

const getResolvedProjectUser = (projectUsersById, projectUserId) => (
  projectUserId ? projectUsersById.get(projectUserId) || null : null
);

const normalizeSlackUser = (slackUser) => {
  const slackUserId = slackUser?.id || slackUser?.slack_user_id || '';

  return createSlackUserMapping({
    id: slackUserId,
    slackUserId,
    slackName: slackUser?.name || slackUser?.slack_user_name || slackUser?.real_name || slackUser?.profile?.real_name || slackUser?.profile?.display_name || '-',
    slackEmail: slackUser?.email || slackUser?.slack_email || slackUser?.profile?.email || '-'
  });
};

const buildSlackUserMappingsFromUsers = ({
  slackUsers = [],
  slackUserMappings = [],
  projectUsers = []
}) => {
  const projectUsersById = createProjectUsersByIdMap(projectUsers);
  const projectUsersByEmail = new Map(
    projectUsers
      .map(normalizeProjectUser)
      .filter(user => user.email && user.email !== '-')
      .map(user => [user.email.trim().toLowerCase(), user])
  );

  const slackMappingsByUserId = new Map(
    slackUserMappings
      .map(normalizeSlackUserMapping)
      .filter(mapping => mapping.slackUserId)
      .map(mapping => [mapping.slackUserId, mapping])
  );

  return slackUsers.map((slackUser) => {
    const normalizedSlackUser = normalizeSlackUser(slackUser);
    const existingMapping = slackMappingsByUserId.get(normalizedSlackUser.slackUserId);
    let userEntry = normalizedSlackUser;

    if (existingMapping?.isMapped && existingMapping?.pmpiUserId) {
      const matchedProjectUser = getResolvedProjectUser(projectUsersById, existingMapping.pmpiUserId);

      userEntry = createSlackUserMapping({
        ...normalizedSlackUser,
        projectUser: matchedProjectUser
          ? {
              id: existingMapping.pmpiUserId,
              name: existingMapping.pmpiName || matchedProjectUser.name || '',
              email: existingMapping.pmpiEmail || matchedProjectUser.email || ''
            }
          : null,
        pmpiUserId: existingMapping.pmpiUserId,
        pmpiName: existingMapping.pmpiName,
        pmpiEmail: existingMapping.pmpiEmail
      });
    }

    if (
      !existingMapping?.is_mapped
      && !userEntry.pmpiUserId
      && userEntry.slackEmail
      && userEntry.slackEmail !== '-'
    ) {
      const matchedByEmail = projectUsersByEmail.get(userEntry.slackEmail.trim().toLowerCase());
      if (matchedByEmail) {
        userEntry = {
          ...userEntry,
          pmpiUserId: matchedByEmail.id,
          pmpiName: matchedByEmail.name,
          pmpiEmail: matchedByEmail.email
        };
      }
    }

    return userEntry;
  });
};

const NotificationActionIconButton = ({
  title,
  onClick,
  disabled,
  className,
  ariaLabel,
  children
}) => (
  <Tooltip title={title} arrow slotProps={APP_TOOLTIP_SLOT_PROPS}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        className={className}
        aria-label={ariaLabel}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const ProjectSelect = ({
  value,
  onChange,
  disabled,
  projects,
  placeholder
}) => (
  <FormControl className={`notification-select notification-select--project ${value ? 'selected' : ''}`}>
    <Select
      value={value || ''}
      onChange={onChange}
      displayEmpty
      className="notification-select-input"
      disabled={disabled}
      renderValue={(selected) => {
        if (!selected) {
          return placeholder;
        }

        return projects.find(project => String(project.id) === String(selected))?.name || placeholder;
      }}
      MenuProps={{
        PaperProps: {
          style: {
            maxHeight: 240,
            maxWidth: 320
          }
        }
      }}
    >
      <MenuItem value="" disabled style={{ display: 'none' }}>
        {placeholder}
      </MenuItem>
      {projects.map(project => (
        <MenuItem key={project.id} value={project.id} className="notification-menu-item" sx={{ minWidth: 0 }}>
          <OverflowTooltip
            title={project.name}
            tooltipWidth={350}
            className="overflow-tooltip-dropdown"
            placement="top-start"
          />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const TeamNotifications = ({
  selectedProjectId = null,
  onProjectChange = () => {}
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const searchParamsTuple = useSearchParams();
  const [searchParams, setSearchParams] = Array.isArray(searchParamsTuple)
    ? searchParamsTuple
    : [new URLSearchParams(), () => {}];

  const {
    isEnabled: slackEnabled,
    isConnected: slackConnected,
    workspace,
    projectConfig,
    loading: slackLoading,
    userMappings,
    userMappingsLoading,
    userMappingsSaving,
    projectConfigLoading,
    projectConfigSaving
  } = useSelector(state => state.slack);
  const { projectDropdownsByUser, projectDropdownsByUserLoading } = useSelector(state => state.tickets);
  const { allUsers, allUsersLoading } = useSelector(state => state.users);

  const projectOptions = useMemo(
    () => (projectDropdownsByUser || [])
      .map(project => ({ id: project?.id, name: project?.name || '' }))
      .filter(project => project.id && project.name),
    [projectDropdownsByUser]
  );

  const initialProjectConfig = normalizeProjectSlackConfig(projectConfig);
  const initialSlackFrequencySelection = getSlackFrequencySelection(initialProjectConfig?.batch_interval_minutes);

  const [selectedTeamPauseChannels, setSelectedTeamPauseChannels] = useState([]);
  const [slackNotificationSettings, setSlackNotificationSettings] = useState(() => (
    initialProjectConfig
      ? normalizeSlackNotificationSettings(initialProjectConfig)
      : createDefaultSlackNotificationSettings()
  ));
  const [slackNotificationFrequency, setSlackNotificationFrequency] = useState(
    initialSlackFrequencySelection.frequency
  );
  const [customSlackNotificationFrequency, setCustomSlackNotificationFrequency] = useState(
    initialSlackFrequencySelection.customFrequency
  );
  const [confirmedCustomSlackNotificationFrequency, setConfirmedCustomSlackNotificationFrequency] = useState(
    initialSlackFrequencySelection.customFrequency
  );
  const [isSlackFrequencyMenuOpen, setIsSlackFrequencyMenuOpen] = useState(false);
  const [isRefreshingUserMappings, setIsRefreshingUserMappings] = useState(false);
  const [slackUsers, setSlackUsers] = useState([]);
  const [editableUserMappings, setEditableUserMappings] = useState([]);
  const [isEditingUserMappings, setIsEditingUserMappings] = useState(false);
  const [isEditingCustomSlackNotificationFrequency, setIsEditingCustomSlackNotificationFrequency] = useState(false);
  const [showMappingWarning, setShowMappingWarning] = useState(false);
  const [pendingMappingChange, setPendingMappingChange] = useState(null);
  const [hasShownMappingWarning, setHasShownMappingWarning] = useState(false);

  const isConfirmingCustomSlackNotificationFrequencyRef = useRef(false);
  const keepSlackFrequencyMenuOpenRef = useRef(false);
  const latestProjectConfigRef = useRef(projectConfig);
  const projectConfigLoadRef = useRef({ key: null, inFlight: false, requestId: 0 });
  const selectedTeamPauseChannelsRef = useRef([]);
  const lastPersistedTeamPauseChannelsRef = useRef([]);
  const teamPauseSaveTimeoutRef = useRef(null);
  const slackNotificationSaveTimeoutRef = useRef(null);
  const lastPersistedSlackNotificationSettingsRef = useRef(slackNotificationSettings);
  const ensuredSlackProjectConfigRef = useRef(new Set());

  const slackStatus = searchParams.get('slack_status');
  const slackMessage = searchParams.get('slack_message');
  const slackTeamName = searchParams.get('slack_team_name');
  const slackAuthCode = searchParams.get('code');
  const slackAuthState = searchParams.get('state');
  const slackAuthError = searchParams.get('error');
  const slackAuthErrorDescription = searchParams.get('error_description');

  const slackProjectBusy = projectConfigLoading || projectConfigSaving;
  const applyProjectSlackState = useCallback((payload) => {
    const nextProjectConfig = normalizeProjectSlackConfig(payload);
    const nextSlackNotificationSettings = normalizeSlackNotificationSettings(nextProjectConfig);
    const { frequency, customFrequency } = getSlackFrequencySelection(nextProjectConfig?.batch_interval_minutes);

    latestProjectConfigRef.current = nextProjectConfig;
    lastPersistedSlackNotificationSettingsRef.current = nextSlackNotificationSettings;
    setSlackNotificationSettings(nextSlackNotificationSettings);
    setSlackNotificationFrequency(frequency);
    setCustomSlackNotificationFrequency(customFrequency);
    setConfirmedCustomSlackNotificationFrequency(customFrequency);
    setIsEditingCustomSlackNotificationFrequency(false);
  }, []);

  const normalizeTeamPauseChannels = useCallback((pauseChannels) => {
    const allowedValues = new Set([
      'email',
      'in_app',
      ...(slackEnabled ? ['slack'] : [])
    ]);

    if (pauseChannels === null || pauseChannels === undefined) {
      return [];
    }

    return Array.isArray(pauseChannels)
      ? pauseChannels.filter(channel => allowedValues.has(channel))
      : [];
  }, [slackEnabled]);

  const teamPauseChannelOptions = useMemo(() => {
    const options = [
      { value: 'email', labelKey: 'notificationSettings.table.headerEmail' },
      { value: 'in_app', labelKey: 'notificationSettings.table.headerInApp' }
    ];

    if (slackEnabled) {
      options.push({ value: 'slack', labelKey: 'notificationSettings.slack' });
    }

    return options;
  }, [slackEnabled]);

  const buildManualUserMappingsPayload = useCallback(() => {
    const seenUserIds = new Set();

    for (const row of editableUserMappings) {
      if (!row.pmpiUserId) {
        continue;
      }

      if (seenUserIds.has(row.pmpiUserId)) {
        toast.error(t('notificationSettings.duplicateSlackUserMapping'));
        return null;
      }

      seenUserIds.add(row.pmpiUserId);
    }

    return editableUserMappings
      .filter(row => row.pmpiUserId && row.slackUserId)
      .map(row => ({
        user_id: row.pmpiUserId,
        slack_user_id: row.slackUserId
      }));
  }, [editableUserMappings, t]);

  const baseUserMappings = useMemo(
    () => buildSlackUserMappingsFromUsers({
      slackUsers,
      slackUserMappings: userMappings || [],
      projectUsers: allUsers || []
    }),
    [allUsers, slackUsers, userMappings]
  );

  const displayedUserMappings = useMemo(
    () => (isEditingUserMappings ? editableUserMappings : baseUserMappings),
    [baseUserMappings, editableUserMappings, isEditingUserMappings]
  );

  const normalizedProjectUsers = useMemo(
    () => (allUsers || []).map(normalizeProjectUser),
    [allUsers]
  );

  const normalizedProjectUsersById = useMemo(
    () => createProjectUsersByIdMap(allUsers || []),
    [allUsers]
  );

  const getProjectUserNameById = useCallback((projectUserId) => (
    normalizedProjectUsersById.get(projectUserId)?.name || '-'
  ), [normalizedProjectUsersById]);

  const getProjectUserOptionsForRow = useCallback((rowId) => {
    const selectedUserIds = new Set(
      editableUserMappings
        .filter(row => row.id !== rowId && row.pmpiUserId)
        .map(row => row.pmpiUserId)
    );

    return normalizedProjectUsers.filter(user => !selectedUserIds.has(user.id));
  }, [editableUserMappings, normalizedProjectUsers]);

  const renderMappingTableValue = useCallback((value) => {
    const displayValue = value || '-';

    return (
      <OverflowTooltip
        title={displayValue}
        tooltipWidth={350}
        className="notification-table-cell-content"
        placement="top-start"
        maxLines={2}
      />
    );
  }, []);

  const canConfirmCustomSlackNotificationFrequency = useMemo(() => {
    const trimmed = String(customSlackNotificationFrequency || '').trim();
    const parsed = parseInt(trimmed, 10);
    return CUSTOM_SLACK_FREQUENCY_REGEX.test(trimmed)
      && Number.isFinite(parsed)
      && parsed > 0
      && parsed <= MAX_CUSTOM_FREQUENCY_MINUTES;
  }, [customSlackNotificationFrequency]);

  const slackNotificationFrequencyOptions = useMemo(() => {
    const options = [...SLACK_NOTIFICATION_FREQUENCY_OPTIONS];
    if (confirmedCustomSlackNotificationFrequency) {
      options.splice(options.length - 1, 0, {
        value: getCustomSlackFrequencyOptionValue(confirmedCustomSlackNotificationFrequency),
        label: formatCustomSlackFrequencyLabel(confirmedCustomSlackNotificationFrequency)
      });
    }
    return options;
  }, [confirmedCustomSlackNotificationFrequency]);

  const slackNotificationFrequencyLabelByValue = useMemo(
    () => slackNotificationFrequencyOptions.reduce((acc, option) => {
      acc[option.value] = option.label || t(option.labelKey);
      return acc;
    }, {}),
    [slackNotificationFrequencyOptions, t]
  );

  const shouldShowCustomSlackFrequencyActions = (
    slackNotificationFrequency === 'custom'
    && isEditingCustomSlackNotificationFrequency
  );

  const shouldShowSlackDisconnectedEmptyState = !slackConnected;

  const persistTeamPauseChannels = useCallback(async (nextChannels) => {
    if (!selectedProjectId) {
      return;
    }

    const normalizedChannels = normalizeTeamPauseChannels(nextChannels);

    try {
      await api.post(ENDPOINTS.SAVE_USER_PREFERENCES, {
        preference_key: 'user_notifications',
        preference_value: {
          notification_config: {
            pause_notifications: normalizedChannels
          }
        }
      }, {
        params: {
          preference_type: 'PROJECT',
          project_id: selectedProjectId
        }
      });

      lastPersistedTeamPauseChannelsRef.current = normalizedChannels;
    } catch (error) {
      if (JSON.stringify(selectedTeamPauseChannelsRef.current) === JSON.stringify(normalizedChannels)) {
        const rollbackChannels = lastPersistedTeamPauseChannelsRef.current;
        selectedTeamPauseChannelsRef.current = rollbackChannels;
        setSelectedTeamPauseChannels(rollbackChannels);
      }

      toast.error(error?.response?.data?.message || error?.response?.data?.error || error.message || t('notificationSettings.errors.saveFailed'));
    }
  }, [normalizeTeamPauseChannels, selectedProjectId, t]);

  const schedulePersistTeamPauseChannels = useCallback((nextChannels) => {
    if (teamPauseSaveTimeoutRef.current) {
      clearTimeout(teamPauseSaveTimeoutRef.current);
    }

    teamPauseSaveTimeoutRef.current = setTimeout(() => {
      teamPauseSaveTimeoutRef.current = null;
      persistTeamPauseChannels(nextChannels);
    }, TEAM_PAUSE_NOTIFICATION_PERSIST_DEBOUNCE_MS);
  }, [persistTeamPauseChannels]);

  const persistSlackProjectConfig = useCallback(async ({
    nextSettings = slackNotificationSettings,
    nextFrequency = slackNotificationFrequency,
    nextCustomFrequency = customSlackNotificationFrequency
  } = {}) => {
    if (!selectedProjectId) {
      toast.error(t('notificationSettings.selectProjectForSlack'));
      return false;
    }

    let batchIntervalMinutes = SLACK_FREQUENCY_TO_MINUTES[nextFrequency];
    const isCustomFrequencySelection = nextFrequency === 'custom' || nextFrequency.startsWith('custom:');

    if (isCustomFrequencySelection) {
      const customFrequencyValue = nextFrequency === 'custom'
        ? nextCustomFrequency
        : String(nextFrequency).slice(7);
      const trimmedCustomFrequency = String(customFrequencyValue || '').trim();
      const parsedCustomFrequency = parseInt(trimmedCustomFrequency, 10);
      if (
        !CUSTOM_SLACK_FREQUENCY_REGEX.test(trimmedCustomFrequency)
        || !Number.isFinite(parsedCustomFrequency)
        || parsedCustomFrequency <= 0
        || parsedCustomFrequency > MAX_CUSTOM_FREQUENCY_MINUTES
      ) {
        toast.error(t('notificationSettings.invalidSlackCustomFrequency'));
        return false;
      }
      batchIntervalMinutes = parsedCustomFrequency;
    }

    try {
      await dispatch(upsertSlackProjectConfig({
        projectId: selectedProjectId,
        config: {
          notification_setting: buildSlackNotificationSettingPayload(nextSettings),
          batch_interval_minutes: batchIntervalMinutes,
          is_active: true
        }
      })).unwrap();

      return true;
    } catch (error) {
      toast.error(error);
      return false;
    }
  }, [
    customSlackNotificationFrequency,
    dispatch,
    selectedProjectId,
    slackNotificationFrequency,
    slackNotificationSettings,
    t
  ]);



  useEffect(() => {
    let isActive = true;

    const loadSlackData = async () => {
      try {
        await dispatch(fetchSlackStatus()).unwrap();
        if (!isActive) {
          return;
        }
      } catch (error) {
        if (isActive) {
          toast.error(error);
        }
      }
    };

    loadSlackData();

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!slackStatus) {
      return undefined;
    }

    if (slackStatus === 'success') {
      toast.success(
        slackTeamName
          ? t('notificationSettings.slackConnectedTeam', { teamName: slackTeamName })
          : t('notificationSettings.slackConnected')
      );
    } else {
      toast.error(slackMessage || t('notificationSettings.slackConnectionFailed'));
    }

    setSearchParams({}, { replace: true });
    return undefined;
  }, [setSearchParams, slackMessage, slackStatus, slackTeamName, t]);

  useEffect(() => {
    if (!slackAuthCode && !slackAuthState && !slackAuthError) {
      return undefined;
    }

    let isActive = true;

    const completeSlackAuth = async () => {
      if (slackAuthError) {
        if (isActive) {
          toast.error(slackAuthErrorDescription || slackAuthError);
          setSearchParams({}, { replace: true });
        }
        return;
      }

      if (!slackAuthCode || !slackAuthState) {
        return;
      }

      try {
        await dispatch(handleSlackCallback({ code: slackAuthCode, state: slackAuthState })).unwrap();
        if (isActive) {
          toast.success(t('notificationSettings.slackConnected'));
          setSearchParams({}, { replace: true });
        }
      } catch (callbackError) {
        if (isActive) {
          toast.error(callbackError);
          setSearchParams({}, { replace: true });
        }
      }
    };

    completeSlackAuth();

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    setSearchParams,
    slackAuthCode,
    slackAuthError,
    slackAuthErrorDescription,
    slackAuthState,
    t
  ]);

  useEffect(() => {
    if (!slackConnected) {
      return undefined;
    }

    let isActive = true;

    const loadGlobalMappings = async () => {
      try {
        const [slackUsersResponse] = await Promise.all([
          dispatch(fetchSlackUsers()).unwrap(),
          dispatch(fetchSlackUserMappings()).unwrap(),
          dispatch(fetchAllUsers()).unwrap()
        ]);
        const fetchedSlackUsers = slackUsersResponse?.data?.users || slackUsersResponse?.users || [];
        if (isActive) {
          setSlackUsers(fetchedSlackUsers);
        }
      } catch (error) {
        if (isActive) {
          toast.error(error);
        }
      }
    };

    loadGlobalMappings();

    return () => {
      isActive = false;
    };
  }, [dispatch, slackConnected]);

  useEffect(() => {
    if (slackConnected) {
      return undefined;
    }

    setEditableUserMappings([]);
    setSlackUsers([]);
    setSlackNotificationSettings(createDefaultSlackNotificationSettings());
    setSlackNotificationFrequency('all_time');
    setCustomSlackNotificationFrequency('');
    setConfirmedCustomSlackNotificationFrequency('');
    setIsEditingCustomSlackNotificationFrequency(false);
    setSelectedTeamPauseChannels([]);
    return undefined;
  }, [slackConnected]);

  useEffect(() => {
    let isActive = true;

    if (!selectedProjectId || !slackConnected) {
      setSlackNotificationSettings(createDefaultSlackNotificationSettings());
      setSlackNotificationFrequency('all_time');
      setCustomSlackNotificationFrequency('');
      setConfirmedCustomSlackNotificationFrequency('');
      setIsEditingCustomSlackNotificationFrequency(false);
      setSelectedTeamPauseChannels([]);
      projectConfigLoadRef.current = { key: null, inFlight: false, requestId: 0 };
      return () => {
        isActive = false;
      };
    }

    const requestKey = String(selectedProjectId);
    if (
      projectConfigLoadRef.current.inFlight
      && projectConfigLoadRef.current.key === requestKey
    ) {
      return () => {
        isActive = false;
      };
    }

    const requestId = projectConfigLoadRef.current.requestId + 1;
    projectConfigLoadRef.current = {
      key: requestKey,
      inFlight: true,
      requestId
    };

    const loadProjectSlackConfig = async () => {
      try {
        const response = await dispatch(fetchSlackProjectConfig(selectedProjectId)).unwrap();
        const fetchedProjectConfig = normalizeProjectSlackConfig(response);
        const resolvedProjectConfig = fetchedProjectConfig || latestProjectConfigRef.current;

        if (!isActive) {
          return;
        }

        if (resolvedProjectConfig) {
          if (
            resolvedProjectConfig?.slack_channel_id === null
            && !ensuredSlackProjectConfigRef.current.has(requestKey)
          ) {
            ensuredSlackProjectConfigRef.current.add(requestKey);
            await dispatch(upsertSlackProjectConfig({
              projectId: selectedProjectId,
              config: {
                batch_interval_minutes: null
              }
            })).unwrap();
          }

          applyProjectSlackState(resolvedProjectConfig);
        } else {
          setSlackNotificationSettings(createDefaultSlackNotificationSettings());
          setSlackNotificationFrequency('all_time');
          setCustomSlackNotificationFrequency('');
          setConfirmedCustomSlackNotificationFrequency('');
          setIsEditingCustomSlackNotificationFrequency(false);
        }
      } catch (error) {
        if (isActive) {
          toast.error(error);
        }
      } finally {
        if (projectConfigLoadRef.current.requestId === requestId) {
          projectConfigLoadRef.current = {
            key: requestKey,
            inFlight: false,
            requestId
          };
        }
      }
    };

    loadProjectSlackConfig();

    return () => {
      isActive = false;
    };
  }, [applyProjectSlackState, dispatch, selectedProjectId, slackConnected]);

  useEffect(() => {
    if (teamPauseSaveTimeoutRef.current) {
      clearTimeout(teamPauseSaveTimeoutRef.current);
      teamPauseSaveTimeoutRef.current = null;
    }
  }, [selectedProjectId]);

  useEffect(() => {
    ensuredSlackProjectConfigRef.current = new Set();
  }, [selectedProjectId]);

  useEffect(() => () => {
    if (teamPauseSaveTimeoutRef.current) {
      clearTimeout(teamPauseSaveTimeoutRef.current);
    }
    if (slackNotificationSaveTimeoutRef.current) {
      clearTimeout(slackNotificationSaveTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!selectedProjectId || !slackConnected) {
      return undefined;
    }

    let isActive = true;

    const loadPauseChannels = async () => {
      try {
        const response = await api.get(ENDPOINTS.USER_PREFERENCES('user_notifications'), {
          params: {
            preference_type: 'PROJECT',
            project_id: selectedProjectId
          }
        });

        const notificationConfig = response?.data?.data?.notification_config;
        const pauseNotifications = notificationConfig?.notification_config?.pause_notifications ?? notificationConfig?.pause_notifications;
        const nextChannels = normalizeTeamPauseChannels(pauseNotifications);

        if (!isActive) {
          return;
        }

        selectedTeamPauseChannelsRef.current = nextChannels;
        lastPersistedTeamPauseChannelsRef.current = nextChannels;
        setSelectedTeamPauseChannels(nextChannels);
      } catch (error) {
        if (!isActive) {
          return;
        }

        selectedTeamPauseChannelsRef.current = [];
        lastPersistedTeamPauseChannelsRef.current = [];
        setSelectedTeamPauseChannels([]);
      }
    };

    loadPauseChannels();

    return () => {
      isActive = false;
    };
  }, [normalizeTeamPauseChannels, selectedProjectId, slackConnected]);

  const handleConnectSlack = useCallback(async () => {
    if (slackLoading) {
      return;
    }

    if (slackConnected) {
      toast.info(t('notificationSettings.slackAlreadyConnected'));
      return;
    }

    try {
      const result = await dispatch(connectSlack('admin')).unwrap();
      const authUrl =
        result?.data?.authUrl
        || result?.authUrl
        || result?.data?.authorization_url
        || result?.authorization_url;

      if (authUrl) {
        if (selectedProjectId) {
          sessionStorage.setItem('slack_connect_project_id', String(selectedProjectId));
        }
        window.location.href = authUrl;
      } else {
        toast.error(t('notificationSettings.slackAuthUrlMissing'));
      }
    } catch (error) {
      toast.error(error?.message || error);
    }
  }, [dispatch, selectedProjectId, slackConnected, slackLoading, t]);

  const handleDisconnectSlack = useCallback(async () => {
    try {
      await dispatch(disconnectSlack()).unwrap();
      await dispatch(fetchSlackStatus()).unwrap();
      toast.success(t('notificationSettings.slackDisconnected'));
      setEditableUserMappings([]);
    } catch (error) {
      toast.error(error);
    }
  }, [dispatch, t]);

  const handleTeamPauseChannelsChange = useCallback((event) => {
    let newValue = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    newValue = newValue.filter(value => value !== '' && value != null);

    const nextChannels = normalizeTeamPauseChannels(
      newValue.includes('none') ? [] : newValue
    );

    if (JSON.stringify(selectedTeamPauseChannelsRef.current) === JSON.stringify(nextChannels)) {
      return;
    }

    selectedTeamPauseChannelsRef.current = nextChannels;
    setSelectedTeamPauseChannels(nextChannels);
    schedulePersistTeamPauseChannels(nextChannels);
  }, [normalizeTeamPauseChannels, schedulePersistTeamPauseChannels]);

  const handleSlackNotificationFrequencyMenuOpen = useCallback(() => {
    setIsSlackFrequencyMenuOpen(true);
  }, []);

  const handleSlackNotificationFrequencyMenuClose = useCallback(() => {
    if (keepSlackFrequencyMenuOpenRef.current) {
      keepSlackFrequencyMenuOpenRef.current = false;
      return;
    }

    setIsSlackFrequencyMenuOpen(false);

    if (slackNotificationFrequency === 'custom' && isEditingCustomSlackNotificationFrequency) {
      applyProjectSlackState(latestProjectConfigRef.current);
    }
  }, [
    applyProjectSlackState,
    isEditingCustomSlackNotificationFrequency,
    slackNotificationFrequency
  ]);

  const handleSlackNotificationFrequencyChange = useCallback(async (event) => {
    const nextFrequency = event.target.value;
    const previousFrequency = slackNotificationFrequency;
    const previousCustomFrequency = customSlackNotificationFrequency;
    const previousConfirmedCustomFrequency = confirmedCustomSlackNotificationFrequency;

    if (nextFrequency === 'custom') {
      keepSlackFrequencyMenuOpenRef.current = true;
      setSlackNotificationFrequency(nextFrequency);
      setCustomSlackNotificationFrequency(confirmedCustomSlackNotificationFrequency || '');
      setIsEditingCustomSlackNotificationFrequency(true);
      return;
    }

    setSlackNotificationFrequency(nextFrequency);
    if (!String(nextFrequency).startsWith('custom:')) {
      setCustomSlackNotificationFrequency('');
      setConfirmedCustomSlackNotificationFrequency('');
      setIsEditingCustomSlackNotificationFrequency(false);
    }

    if (!selectedProjectId) {
      return;
    }

    const saved = await persistSlackProjectConfig({
      nextFrequency,
      nextCustomFrequency: ''
    });

    if (!saved) {
      setSlackNotificationFrequency(previousFrequency);
      setCustomSlackNotificationFrequency(previousCustomFrequency);
      setConfirmedCustomSlackNotificationFrequency(previousConfirmedCustomFrequency);
    } else {
      setConfirmedCustomSlackNotificationFrequency('');
      setIsSlackFrequencyMenuOpen(false);
    }
  }, [
    confirmedCustomSlackNotificationFrequency,
    customSlackNotificationFrequency,
    persistSlackProjectConfig,
    selectedProjectId,
    slackNotificationFrequency
  ]);

  const handleCustomSlackNotificationFrequencyChange = useCallback((event) => {
    const nextValue = event.target.value.trim();
    if (nextValue === '' || CUSTOM_SLACK_FREQUENCY_INPUT_REGEX.test(nextValue)) {
      setCustomSlackNotificationFrequency(nextValue);
      setIsEditingCustomSlackNotificationFrequency(true);
    }
  }, []);

  const handleConfirmCustomSlackNotificationFrequency = useCallback(async () => {
    if (
      !selectedProjectId
      || slackNotificationFrequency !== 'custom'
      || isConfirmingCustomSlackNotificationFrequencyRef.current
    ) {
      return;
    }

    isConfirmingCustomSlackNotificationFrequencyRef.current = true;

    try {
      const saved = await persistSlackProjectConfig({
        nextFrequency: 'custom',
        nextCustomFrequency: customSlackNotificationFrequency
      });

      if (saved) {
        setConfirmedCustomSlackNotificationFrequency(customSlackNotificationFrequency);
        setSlackNotificationFrequency(getCustomSlackFrequencyOptionValue(customSlackNotificationFrequency));
        setIsEditingCustomSlackNotificationFrequency(false);
        setIsSlackFrequencyMenuOpen(false);
      }
    } finally {
      isConfirmingCustomSlackNotificationFrequencyRef.current = false;
    }
  }, [customSlackNotificationFrequency, persistSlackProjectConfig, selectedProjectId, slackNotificationFrequency]);

  const handleCancelCustomSlackNotificationFrequency = useCallback(() => {
    setIsSlackFrequencyMenuOpen(false);
    applyProjectSlackState(latestProjectConfigRef.current);
  }, [applyProjectSlackState]);

  const handleCustomSlackNotificationFrequencyKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirmCustomSlackNotificationFrequency();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelCustomSlackNotificationFrequency();
    }
  }, [handleCancelCustomSlackNotificationFrequency, handleConfirmCustomSlackNotificationFrequency]);

  const handleUserMappingSelectionChange = useCallback((rowId, nextProjectUserId) => {
    try {
      if (nextProjectUserId && !hasShownMappingWarning) {
        setPendingMappingChange({ rowId, nextProjectUserId });
        setShowMappingWarning(true);
        return;
      }

      setEditableUserMappings(prev => prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const selectedProjectUser = nextProjectUserId ? normalizedProjectUsersById.get(nextProjectUserId) : null;
        return {
          ...row,
          pmpiUserId: selectedProjectUser?.id || '',
          pmpiName: selectedProjectUser?.name || '',
          pmpiEmail: selectedProjectUser?.email || ''
        };
      }));
    } catch (error) {
      toast.error(error?.message || 'An error occurred while changing mapping');
    }
  }, [hasShownMappingWarning, normalizedProjectUsersById]);

  const handleConfirmMappingWarning = useCallback(() => {
    setHasShownMappingWarning(true);
    setShowMappingWarning(false);

    if (pendingMappingChange) {
      const { rowId, nextProjectUserId } = pendingMappingChange;
      handleUserMappingSelectionChange(rowId, nextProjectUserId);
      setPendingMappingChange(null);
    }
  }, [handleUserMappingSelectionChange, pendingMappingChange]);

  const handleCancelMappingWarning = useCallback(() => {
    setShowMappingWarning(false);
    setPendingMappingChange(null);
  }, []);

  const handleConfirmMapping = useCallback(async () => {
    try {
      const manualMappingsPayload = buildManualUserMappingsPayload();
      if (manualMappingsPayload === null) {
        return;
      }

      if (manualMappingsPayload.length > 0) {
        await dispatch(updateSlackUserMappings(manualMappingsPayload)).unwrap();
      }

      await Promise.all([
        dispatch(fetchSlackUserMappings()).unwrap(),
        dispatch(fetchSlackUsers()).unwrap(),
        dispatch(fetchAllUsers()).unwrap()
      ]);

      setEditableUserMappings(baseUserMappings);
      setIsEditingUserMappings(false);
      toast.success(t('notificationSettings.slackMappingConfirmed'));
    } catch (error) {
      toast.error(error);
    }
  }, [baseUserMappings, buildManualUserMappingsPayload, dispatch, t]);

  const handleCancelUserMappingEdit = useCallback(() => {
    setEditableUserMappings(baseUserMappings);
    setIsEditingUserMappings(false);
  }, [baseUserMappings]);

  const handleSyncUsers = useCallback(async () => {
    try {
      setIsRefreshingUserMappings(true);
      const [latestSlackUsersResponse, latestUserMappingsResponse, latestUsersResponse] = await Promise.all([
        dispatch(fetchSlackUsers()).unwrap(),
        dispatch(fetchSlackUserMappings()).unwrap(),
        dispatch(fetchAllUsers()).unwrap()
      ]);

      const latestSlackUsers = latestSlackUsersResponse?.data?.users || latestSlackUsersResponse?.users || [];
      const latestUserMappings = latestUserMappingsResponse?.data?.mappings || latestUserMappingsResponse?.mappings || [];
      const latestUsers = latestUsersResponse?.data?.users || latestUsersResponse?.users || latestUsersResponse || [];

      setSlackUsers(latestSlackUsers);
      setEditableUserMappings(buildSlackUserMappingsFromUsers({
        slackUsers: latestSlackUsers,
        slackUserMappings: latestUserMappings,
        projectUsers: latestUsers
      }));
      setIsEditingUserMappings(true);
    } catch (error) {
      toast.error(error);
    } finally {
      setIsRefreshingUserMappings(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (userMappingsLoading || allUsersLoading || isEditingUserMappings) {
      return;
    }

    setEditableUserMappings(baseUserMappings);
  }, [allUsersLoading, baseUserMappings, isEditingUserMappings, userMappingsLoading]);

  useEffect(() => {
    if (selectedProjectId || !slackConnected || projectOptions.length === 0) {
      return;
    }

    onProjectChange(projectOptions[0].id);
  }, [onProjectChange, projectOptions, selectedProjectId, slackConnected]);

  const workspaceName = workspace?.name || workspace?.team_name;
  const projectPlaceholder = projectDropdownsByUserLoading
    ? t('common.loading')
    : t('notificationSettings.allProjects');

  return (
    <>
      <div className="notification-row notification-row--team-actions notification-row--team-config">
        <Card elevation={0} className="notification-card notification-card--team-project">
          <CardContent className="notification-card-content">
            <Box className="notification-team-project-panel">
              <div className="notification-card-row notification-team-project-row notification-team-project-row--header">
                <Typography variant="body1" className="notification-card-title">
                  {t('notificationSettings.project')}
                </Typography>
                <ProjectSelect
                  value={selectedProjectId || ''}
                  onChange={(event) => onProjectChange(event.target.value)}
                  disabled={!slackConnected || projectDropdownsByUserLoading || slackProjectBusy || projectOptions.length === 0}
                  projects={projectOptions}
                  placeholder={projectPlaceholder}
                />
              </div>

              <Box className="notification-team-project-utilities">
                <div className="notification-card-row notification-team-project-row notification-team-project-row--stacked">
                  <Typography variant="body1" className="notification-card-title">
                    {t('notificationSettings.pauseTeamNotifications')}
                  </Typography>
                  <FormControl className={`notification-select ${selectedTeamPauseChannels?.length > 0 ? 'selected' : ''}`}>
                    <Select
                      multiple
                      value={selectedTeamPauseChannels || []}
                      onChange={handleTeamPauseChannelsChange}
                      displayEmpty
                      className="notification-select-input"
                      disabled={!slackConnected || slackProjectBusy}
                      renderValue={(selected) => {
                        if (selected.length === 0) return t('notificationSettings.none');
                        if (selected.length === 1) {
                          const option = teamPauseChannelOptions.find(opt => opt.value === selected[0]);
                          return option ? t(option.labelKey) : selected[0];
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      <MenuItem value="none" className="notification-menu-item">
                        {t('notificationSettings.none')}
                      </MenuItem>
                      {teamPauseChannelOptions.map(option => (
                        <MenuItem key={option.value} value={option.value} className="notification-menu-item">
                          {t(option.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className="notification-card-row notification-team-project-row notification-team-project-row--stacked">
                  <Typography variant="body1" className="notification-card-title">
                    {t('notificationSettings.slackNotificationFrequency')}
                  </Typography>
                  <FormControl className={`notification-select notification-select--schedule ${slackNotificationFrequency ? 'selected' : ''}`}>
                    <Select
                      value={slackNotificationFrequency}
                      onChange={handleSlackNotificationFrequencyChange}
                      open={isSlackFrequencyMenuOpen}
                      onOpen={handleSlackNotificationFrequencyMenuOpen}
                      onClose={handleSlackNotificationFrequencyMenuClose}
                      className="notification-select-input"
                      disabled={!slackConnected || slackProjectBusy}
                      renderValue={(selected) => (
                        slackNotificationFrequencyLabelByValue[selected]
                        || (selected === 'custom'
                          ? t('notificationSettings.slackFrequency.customTime')
                          : '')
                      )}
                      MenuProps={{
                        PaperProps: {
                          className: 'notification-frequency-menu'
                        }
                      }}
                    >
                      {slackNotificationFrequencyOptions.flatMap((option) => {
                        const items = [
                          (
                            <MenuItem key={option.value} value={option.value} className="notification-menu-item">
                              {option.label || t(option.labelKey)}
                            </MenuItem>
                          )
                        ];

                        if (option.value === 'custom' && isSlackFrequencyMenuOpen && shouldShowCustomSlackFrequencyActions) {
                          items.push(
                            <MenuItem
                              key="custom-frequency-editor"
                              disableRipple
                              disableTouchRipple
                              tabIndex={-1}
                              className="notification-frequency-custom-editor"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Box className="notification-custom-time-group notification-custom-time-group--menu">
                                <TextField
                                  value={customSlackNotificationFrequency}
                                  onChange={handleCustomSlackNotificationFrequencyChange}
                                  onKeyDown={(event) => {
                                    event.stopPropagation();
                                    handleCustomSlackNotificationFrequencyKeyDown(event);
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  placeholder={t('notificationSettings.slackFrequency.customPlaceholder')}
                                  size="small"
                                  className="notification-custom-time-input notification-custom-time-input--menu"
                                  disabled={slackProjectBusy}
                                  inputProps={{ inputMode: 'numeric', min: 0, pattern: '^\\d*$' }}
                                />
                                <Box className="notification-channel-action-group">
                                  <NotificationActionIconButton
                                    title={t('notificationSettings.confirmCustomFrequency')}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleConfirmCustomSlackNotificationFrequency();
                                    }}
                                    disabled={slackProjectBusy || !canConfirmCustomSlackNotificationFrequency}
                                    className="notification-channel-action-btn notification-channel-action-btn--confirm"
                                    ariaLabel={t('notificationSettings.confirmCustomFrequency')}
                                  >
                                    <CheckOutlined fontSize="small" />
                                  </NotificationActionIconButton>
                                  <NotificationActionIconButton
                                    title={t('notificationSettings.cancelCustomFrequency')}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleCancelCustomSlackNotificationFrequency();
                                    }}
                                    disabled={slackProjectBusy}
                                    className="notification-channel-action-btn notification-channel-action-btn--cancel"
                                    ariaLabel={t('notificationSettings.cancelCustomFrequency')}
                                  >
                                    <CloseOutlined fontSize="small" />
                                  </NotificationActionIconButton>
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        }

                        return items;
                      })}
                    </Select>
                  </FormControl>
                </div>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>

      <div className="notification-slack-section">
        <div className="notification-slack-section-header">
          <Typography variant="body2" className="notification-slack-heading">
            {t('notificationSettings.slackConfiguration')}
          </Typography>
        </div>
        <Card elevation={0} className="notification-card notification-card--slack-status">
          <CardContent className="notification-card-content">
            <div className="notification-card-row notification-slack-status-row-inner">
              <div className="notification-slack-status-left">
                <Typography variant="body1" className="notification-card-title">
                  {t('notificationSettings.connectedWorkspace')}
                </Typography>
                {workspaceName ? (
                  <Box className="notification-workspace-badge">
                    <img
                      src={slackIcon}
                      alt="Slack"
                      className="notification-workspace-badge-icon"
                    />
                    <Typography variant="body1" className="notification-workspace-badge-text">
                      {workspaceName}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" className="notification-card-value">
                    {slackLoading ? t('notificationSettings.loading') : '-'}
                  </Typography>
                )}
              </div>
              <Button
                variant="outlined"
                onClick={slackConnected ? handleDisconnectSlack : handleConnectSlack}
                className="notification-slack-action-btn notification-disconnect-btn"
                startIcon={(
                  <img
                    src={slackIcon}
                    alt="Slack"
                    className="notification-slack-action-icon"
                  />
                )}
              >
                {slackLoading
                  ? t('common.loading')
                  : (slackConnected ? t('notificationSettings.disconnectSlack') : t('notificationSettings.connectSlack'))}
              </Button>
            </div>
          </CardContent>
        </Card>
        <div className="notification-slack-container">
          {projectConfigLoading ? (
            <div className="notification-loading-overlay notification-loading-overlay--content">
              <div className="notification-spinner" />
              <Typography className="notification-loading-text">
                {t('notificationSettings.loadingSettings')}
              </Typography>
            </div>
          ) : shouldShowSlackDisconnectedEmptyState ? (
            <Box className="notification-slack-empty-state">
              <Typography variant="h6" className="notification-slack-empty-title">
                {t('notificationSettings.slackConfigurationConnectTitle')}
              </Typography>
              <Typography className="notification-slack-empty-message">
                {t('notificationSettings.slackConfigurationConnectMessage')}
              </Typography>
            </Box>
          ) : (
            <>
              {slackConnected && (
                <div className="notification-row">
                  <Card elevation={0} className="notification-card notification-card--full">
                    <CardContent className="notification-card-content">
                      <Box className="notification-mapping-container">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="body1" className="notification-card-title">
                            {t('notificationSettings.userMapping')}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSyncUsers}
                            className="notification-sync-btn"
                            disabled={isRefreshingUserMappings || userMappingsLoading || allUsersLoading || userMappingsSaving || slackProjectBusy}
                          >
                            {isRefreshingUserMappings ? t('common.loading') : t('notificationSettings.syncUsers')}
                          </Button>
                        </Box>
                        <TableContainer className="notification-table">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell className="notification-table-header">{t('notificationSettings.pmpiUserName')}</TableCell>
                                <TableCell className="notification-table-header">{t('notificationSettings.pmpiEmail')}</TableCell>
                                <TableCell className="notification-table-header">{t('notificationSettings.slackUserName')}</TableCell>
                                <TableCell className="notification-table-header">{t('notificationSettings.slackEmail')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {userMappingsLoading || allUsersLoading ? (
                                <TableRow>
                                  <TableCell className="notification-table-cell" colSpan={4}>
                                    {t('common.loading')}
                                  </TableCell>
                                </TableRow>
                              ) : displayedUserMappings.length > 0 ? (
                                displayedUserMappings.map((mapping) => (
                                  <TableRow key={mapping.id}>
                                    <TableCell className="notification-table-cell">
                                      {isEditingUserMappings && !areEmailsMatching(mapping.pmpiEmail, mapping.slackEmail) ? (
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={mapping.pmpiUserId || ''}
                                            onChange={(event) => handleUserMappingSelectionChange(mapping.id, event.target.value)}
                                            displayEmpty
                                            className="notification-table-select"
                                            disabled={userMappingsSaving || slackProjectBusy}
                                            MenuProps={{
                                              PaperProps: {
                                                className: 'notification-table-select-menu'
                                              }
                                            }}
                                            renderValue={(selected) => {
                                              if (!selected) {
                                                return '-';
                                              }

                                              let displayName = getProjectUserNameById(selected);
                                              if (displayName === '-' && selected === mapping.pmpiUserId && mapping.pmpiName) {
                                                displayName = mapping.pmpiName;
                                              }

                                              return (
                                                <OverflowTooltip
                                                  title={displayName}
                                                  tooltipWidth={350}
                                                  className="notification-table-select-value"
                                                  placement="top-start"
                                                />
                                              );
                                            }}
                                          >
                                            {(() => {
                                              const projectUserOptions = getProjectUserOptionsForRow(mapping.id);

                                              if (projectUserOptions.length === 0) {
                                                return (
                                                  <MenuItem
                                                    disabled
                                                    value=""
                                                    className="notification-menu-item"
                                                    sx={{ minWidth: 0, whiteSpace: 'normal', cursor: 'default' }}
                                                  >
                                                    <Typography variant="body2">
                                                      {NO_USERS_AVAILABLE_FOR_MAPPING_MESSAGE}
                                                    </Typography>
                                                  </MenuItem>
                                                );
                                              }

                                              return projectUserOptions.map((projectUser) => (
                                                <MenuItem
                                                  key={projectUser.id}
                                                  value={projectUser.id}
                                                  sx={{ minWidth: 0 }}
                                                >
                                                  <OverflowTooltip
                                                    title={projectUser.name}
                                                    tooltipWidth={350}
                                                    className="overflow-tooltip-dropdown"
                                                    placement="top-start"
                                                  />
                                                </MenuItem>
                                              ));
                                            })()}
                                          </Select>
                                        </FormControl>
                                      ) : (
                                        renderMappingTableValue(mapping.pmpiName)
                                      )}
                                    </TableCell>
                                    <TableCell className="notification-table-cell">{renderMappingTableValue(mapping.pmpiEmail)}</TableCell>
                                    <TableCell className="notification-table-cell">{renderMappingTableValue(mapping.slackName)}</TableCell>
                                    <TableCell className="notification-table-cell">{renderMappingTableValue(mapping.slackEmail)}</TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell className="notification-table-cell" colSpan={4}>
                                    {t('notificationSettings.noSlackUsersFound')}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {isEditingUserMappings && !userMappingsLoading && !allUsersLoading && (
                          <Box className="notification-mapping-actions">
                            <Button
                              variant="outlined"
                              onClick={handleCancelUserMappingEdit}
                              className="notification-cancel-btn"
                              disabled={slackProjectBusy || userMappingsSaving}
                              sx={{ marginRight: 1 }}
                            >
                              {t('common.cancel')}
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleConfirmMapping}
                              className="notification-confirm-btn"
                              disabled={slackProjectBusy || userMappingsSaving}
                            >
                              {userMappingsSaving ? t('common.loading') : t('notificationSettings.confirm')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog
        open={showMappingWarning}
        onClose={handleCancelMappingWarning}
        className="project-status-modal"
        disableEscapeKeyDown={false}
      >
        <DialogContent className="project-status-content">
          <Box className="project-status-icon warning">
            <Warning />
          </Box>
          <Typography variant="h4" className="project-status-title">
            {t('notificationSettings.mappingWarning.title')}
          </Typography>
          <Typography className="project-status-message">
            {t('notificationSettings.mappingWarning.message')}
          </Typography>
        </DialogContent>
        <DialogActions className="project-status-actions">
          <Button onClick={handleCancelMappingWarning} variant="outlined" className="cancel-button">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmMappingWarning} variant="contained" className="activate-button warning">
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamNotifications;
